// src/app/api/libraryApi.ts
//
// RTK Query endpoints for the online academy admin tools:
//   • Lesson library — /admin/library/*  (weeks, micro-lessons, scripts, videos)
//   • Pricing        — /admin/pricing    (price per track × delivery mode)
//   • Clusters       — /admin/clusters   (demand per area for on-site)

import { baseApi } from "./baseApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type LibraryTrack = "foundation_builders" | "explorers" | "innovators";
export type DeliveryMode = "online" | "hybrid" | "onsite";

export const STRANDS = [
  "Technology",
  "Leadership & Values",
  "Entrepreneurship & Finance",
  "STEM Lab",
  "AI Literacy",
  "Public Speaking",
  "Showcase",
] as const;

export const EVIDENCE_TYPES = ["photo", "video", "audio", "link", "file", "text"] as const;

export const RUBRIC_AREAS: { value: string; label: string }[] = [
  { value: "technical_quality", label: "Technical quality" },
  { value: "understanding", label: "Understanding" },
  { value: "user_value", label: "User value" },
  { value: "iteration", label: "Iteration" },
  { value: "communication", label: "Communication" },
  { value: "character", label: "Character" },
];

export interface ScriptScene {
  screen: string;
  narration: string;
}

export interface LessonVideo {
  id: string;
  fileName: string | null;
  durationSec: number | null;
  sizeBytes: number | null;
  contentType: string | null;
  source: "generated" | "recorded" | "uploaded";
  hasLowRes: boolean;
  hasCaptions: boolean;
  uploadedAt: string | null;
  uploadedByName: string | null;
  replacedAt: string | null;
}

export interface MicroLesson {
  key: string;
  weekKey: string;
  track: LibraryTrack;
  order: number;
  strand: string;
  title: string;
  objective: string | null;
  watchMinutes: number;
  doMinutes: number;
  uploadMinutes: number;
  totalMinutes: number;
  script: ScriptScene[];
  narrationWords: number;
  action: { steps: string[]; materials: string[]; offScreen: boolean };
  evidence: { types: string[]; prompt: string | null };
  rubricFocus: string[];
  parentNote: string | null;
  /** The in-app workspace the child's Do step offers (null = none) */
  workspace: "scratch" | "web" | "react" | "native" | null;
  status: "draft" | "approved";
  video: LessonVideo | null;
  videoHistory: LessonVideo[];
  updatedAt: string;
}

export interface WeekSummary {
  key: string;
  track: LibraryTrack;
  quarter: string | null;
  stage: string | null;
  term: string;
  weekNumber: number;
  title: string;
  topics: string | null;
  isShowcase: boolean;
  /** Showcase weeks: what the panel scores (custom, or the defaults) */
  showcaseCriteria?: string[] | null;
  showcaseCriteriaCustom?: boolean;
  hybridOnsite: boolean;
  conversionStatus: "not_started" | "draft" | "in_review" | "approved";
  weeklyTarget: { must: string[]; stretch: string[]; source: "lesson_plan" | "edited" };
  parentBrief: string | null;
  homeMaterials: string[];
  session: Record<string, string | undefined>;
  sessionMap: string[];
  sourceFile: string | null;
  lessonPlanWords: number;
  microLessons?: number;
  videos?: number;
  approved?: number;
}

export interface WeekDetail extends Omit<WeekSummary, "microLessons"> {
  lessonPlanHtml: string | null;
  microLessons: MicroLesson[];
  storageConfigured: boolean;
}

export interface TermStats {
  term: string;
  weeks: number;
  convertedWeeks: number;
  microLessons: number;
  videos: number;
}

export interface PricingData {
  fees: Record<LibraryTrack, Record<DeliveryMode, number | null>>;
  openModes: DeliveryMode[];
  onsiteClusterThreshold: number;
  updatedAt: string | null;
}

export interface ClusterRow {
  key: string;
  state: string;
  city: string;
  areas: string[];
  families: number;
  activeChildren: number;
  onlineChildren: number;
  hybridInterest: number;
  onsiteInterest: number;
  readyForOnsite: boolean;
  opening: { id: string; status: "open" | "closed"; modes: AreaMode[] } | null;
}

export type AreaMode = "hybrid" | "onsite";

export interface AreaVenue {
  name: string | null;
  address: string | null;
  sessionDay: string | null;
  sessionTime: string | null;
}

/** Hybrid / on-site opened in one cluster (Phase 8). */
export interface AreaOpening {
  id: string;
  key: string;
  state: string;
  city: string;
  modes: AreaMode[];
  venue: AreaVenue;
  capacity: Partial<Record<AreaMode, number | null>>;
  seats: Partial<Record<AreaMode, { capacity: number | null; taken: number }>>;
  status: "open" | "closed";
  openedAt: string;
  closedAt: string | null;
  notifiedAt: string | null;
}

export interface AreaOpeningBody {
  modes?: AreaMode[];
  venue?: Partial<AreaVenue>;
  capacity?: Partial<Record<AreaMode, number | null>>;
  status?: "open" | "closed";
  notify?: boolean;
}

interface Ok<T> {
  success: boolean;
  message?: string;
  data: T;
}

export type LessonPatch = Partial<
  Pick<
    MicroLesson,
    | "strand"
    | "title"
    | "objective"
    | "watchMinutes"
    | "doMinutes"
    | "uploadMinutes"
    | "script"
    | "action"
    | "evidence"
    | "rubricFocus"
    | "parentNote"
    | "workspace"
    | "status"
    | "order"
  >
>;

export interface LibraryApproval {
  weeks: number;
  approvedWeeks: number;
  lessons: number;
  approvedLessons: number;
}

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const libraryApi = baseApi
  .enhanceEndpoints({ addTagTypes: ["Library", "LibraryWeek", "Pricing", "Clusters"] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getLibrary: builder.query<
        Ok<{ tracks: Record<string, TermStats[]>; approval?: LibraryApproval; storageConfigured: boolean }>,
        void
      >({
        query: () => "/admin/library",
        providesTags: ["Library"],
      }),

      approveAllLessons: builder.mutation<Ok<{ weeks: number; lessons: number }> & { message: string }, void>({
        query: () => ({ url: "/admin/library/approve-all", method: "POST", body: { confirm: true } }),
        invalidatesTags: ["Library", "LibraryWeek"],
      }),

      getLibraryWeeks: builder.query<Ok<WeekSummary[]>, LibraryTrack>({
        query: (track) => `/admin/library/weeks?track=${track}`,
        providesTags: ["Library"],
      }),

      getLibraryWeek: builder.query<Ok<WeekDetail>, string>({
        query: (key) => `/admin/library/weeks/${key}`,
        providesTags: (_r, _e, key) => [{ type: "LibraryWeek", id: key }],
      }),

      updateLibraryWeek: builder.mutation<
        Ok<WeekSummary>,
        { key: string; body: Record<string, unknown> }
      >({
        query: ({ key, body }) => ({
          url: `/admin/library/weeks/${key}`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_r, _e, { key }) => [
          { type: "LibraryWeek", id: key },
          "Library",
        ],
      }),

      addMicroLesson: builder.mutation<
        Ok<MicroLesson>,
        { weekKey: string; body?: LessonPatch }
      >({
        query: ({ weekKey, body }) => ({
          url: `/admin/library/weeks/${weekKey}/lessons`,
          method: "POST",
          body: body ?? {},
        }),
        invalidatesTags: (_r, _e, { weekKey }) => [
          { type: "LibraryWeek", id: weekKey },
          "Library",
        ],
      }),

      updateMicroLesson: builder.mutation<
        Ok<MicroLesson>,
        { key: string; weekKey: string; body: LessonPatch }
      >({
        query: ({ key, body }) => ({
          url: `/admin/library/lessons/${key}`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_r, _e, { weekKey }) => [
          { type: "LibraryWeek", id: weekKey },
          "Library",
        ],
      }),

      deleteMicroLesson: builder.mutation<Ok<null>, { key: string; weekKey: string }>({
        query: ({ key }) => ({ url: `/admin/library/lessons/${key}`, method: "DELETE" }),
        invalidatesTags: (_r, _e, { weekKey }) => [
          { type: "LibraryWeek", id: weekKey },
          "Library",
        ],
      }),

      getVideoUploadUrl: builder.mutation<
        Ok<{ uploadUrl: string; key: string; expiresIn: number }>,
        { key: string; fileName: string; contentType: string; sizeBytes: number }
      >({
        query: ({ key, ...body }) => ({
          url: `/admin/library/lessons/${key}/video/upload-url`,
          method: "POST",
          body,
        }),
      }),

      attachVideo: builder.mutation<
        Ok<MicroLesson>,
        {
          key: string;
          weekKey: string;
          body: {
            key: string;
            durationSec: number | null;
            fileName: string;
            contentType: string;
            source: "generated" | "recorded" | "uploaded";
          };
        }
      >({
        query: ({ key, body }) => ({
          url: `/admin/library/lessons/${key}/video`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_r, _e, { weekKey }) => [
          { type: "LibraryWeek", id: weekKey },
          "Library",
        ],
      }),

      restoreVideo: builder.mutation<
        Ok<MicroLesson>,
        { key: string; weekKey: string; videoId: string }
      >({
        query: ({ key, videoId }) => ({
          url: `/admin/library/lessons/${key}/video/restore`,
          method: "POST",
          body: { videoId },
        }),
        invalidatesTags: (_r, _e, { weekKey }) => [
          { type: "LibraryWeek", id: weekKey },
          "Library",
        ],
      }),

      removeVideo: builder.mutation<Ok<MicroLesson>, { key: string; weekKey: string }>({
        query: ({ key }) => ({
          url: `/admin/library/lessons/${key}/video`,
          method: "DELETE",
        }),
        invalidatesTags: (_r, _e, { weekKey }) => [
          { type: "LibraryWeek", id: weekKey },
          "Library",
        ],
      }),

      getVideoUrl: builder.mutation<
        Ok<{ url: string; expiresIn: number }>,
        { key: string; videoId?: string }
      >({
        query: ({ key, videoId }) => ({
          url: `/admin/library/lessons/${key}/video-url${videoId ? `?videoId=${videoId}` : ""}`,
          method: "GET",
        }),
      }),

      // ── Pricing & clusters ───────────────────────────────────────
      getPricing: builder.query<Ok<PricingData>, void>({
        query: () => "/admin/pricing",
        providesTags: ["Pricing"],
      }),

      updatePricing: builder.mutation<
        Ok<PricingData>,
        Partial<Pick<PricingData, "fees" | "openModes" | "onsiteClusterThreshold">>
      >({
        query: (body) => ({ url: "/admin/pricing", method: "PUT", body }),
        invalidatesTags: ["Pricing", "Clusters"],
      }),

      getClusters: builder.query<
        Ok<{
          threshold: number;
          clusters: ClusterRow[];
          unlocated: { families: number; activeChildren: number };
          totalActiveChildren: number;
        }>,
        void
      >({
        query: () => "/admin/clusters",
        providesTags: ["Clusters"],
      }),

      // ── Area openings (hybrid / on-site, cluster by cluster) ──
      getAreaOpenings: builder.query<Ok<AreaOpening[]>, void>({
        query: () => "/admin/cluster-openings",
        providesTags: ["Clusters"],
      }),
      openArea: builder.mutation<Ok<AreaOpening> & { message: string }, AreaOpeningBody & { state: string; city: string }>({
        query: (body) => ({ url: "/admin/cluster-openings", method: "POST", body }),
        invalidatesTags: ["Clusters"],
      }),
      updateArea: builder.mutation<Ok<AreaOpening> & { message: string }, AreaOpeningBody & { id: string }>({
        query: ({ id, ...body }) => ({ url: `/admin/cluster-openings/${id}`, method: "PATCH", body }),
        invalidatesTags: ["Clusters"],
      }),

      // ── Daily learning job (weekly summaries + behind-target alerts) ──
      runLearningJob: builder.mutation<
        Ok<LearningJobReport>,
        { dryRun: boolean; childId?: string }
      >({
        query: (body) => ({ url: "/admin/learning-job/run", method: "POST", body }),
      }),
    }),
  });

export interface LearningJobReport {
  dryRun: boolean;
  checked: number;
  summaries: number;
  alerts: number;
  rearmed: number;
  skipped: number;
  errors: number;
  log: string[];
}

export const {
  useGetLibraryQuery,
  useGetLibraryWeeksQuery,
  useGetLibraryWeekQuery,
  useUpdateLibraryWeekMutation,
  useAddMicroLessonMutation,
  useUpdateMicroLessonMutation,
  useDeleteMicroLessonMutation,
  useGetVideoUploadUrlMutation,
  useAttachVideoMutation,
  useRestoreVideoMutation,
  useRemoveVideoMutation,
  useGetVideoUrlMutation,
  useGetPricingQuery,
  useUpdatePricingMutation,
  useGetClustersQuery,
  useGetAreaOpeningsQuery,
  useOpenAreaMutation,
  useUpdateAreaMutation,
  useRunLearningJobMutation,
  useApproveAllLessonsMutation,
} = libraryApi;
